import React from 'react';

const Spinner = ({ className = 'h-4 w-4' }) => (
  <svg className={`animate-spin text-white ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const money = (value) => (typeof value === 'number' && isFinite(value) ? `$${value.toFixed(2)}` : '—');

const DOC_LABELS = { invoice: 'Invoice', rcti: 'RCTI' };

// A document is a real, existing doc worth showing when it was found (has a number).
const isRealDoc = (doc) => !!(doc && doc.currentNumber);

const PreviewRow = ({ docKey, doc, checked, onToggle }) => {
  const label = DOC_LABELS[docKey] || docKey;
  const blocked = !doc.regenerable;
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded border ${blocked ? 'border-gray-200 bg-gray-50' : 'border-jims-cyan/40 bg-jims-cyan/5 cursor-pointer'}`}
    >
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 text-jims-cyan focus:ring-jims-cyan disabled:opacity-50"
        checked={checked}
        disabled={blocked}
        onChange={() => onToggle(docKey)}
      />
      <div className="flex-1 text-left">
        <div className="flex items-center justify-between">
          <span className="font-medium text-gray-900">{label}</span>
          <span className="text-sm font-mono text-gray-700">
            {doc.currentNumber} <span className="text-gray-400">→</span> {doc.nextNumber || '—'}
          </span>
        </div>
        {blocked ? (
          <p className="mt-1 text-sm text-amber-700">{doc.blockReason || doc.message || 'Cannot be regenerated.'}</p>
        ) : (
          <p className="mt-1 text-sm text-gray-600">
            Amount: {money(doc.old?.total)} <span className="text-gray-400">→</span>{' '}
            <span className="font-medium text-gray-900">{money(doc.new?.total)}</span>
          </p>
        )}
      </div>
    </label>
  );
};

const ResultRow = ({ docKey, res }) => {
  if (!res) return null;
  const label = DOC_LABELS[docKey] || docKey;
  const ok = res.status === 'success';
  const warnings = res.warnings || (res.warning ? [res.warning] : []);
  return (
    <div className={`p-3 rounded border text-left ${ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
      <p className={`text-sm font-medium ${ok ? 'text-green-800' : 'text-red-800'}`}>
        {ok ? '✓' : '✗'} {label}: {res.message}
      </p>
      {warnings.length > 0 && (
        <ul className="mt-1 ml-4 list-disc text-xs text-amber-700">
          {warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}
    </div>
  );
};

/**
 * Confirmation modal shown after a completed job's billing inputs are edited —
 * price, hours or completion date: lets the user regenerate the affected draft
 * invoice/RCTI (reusing the number with a "(n)" suffix) or skip. Renders a
 * preview first, then execution results.
 */
const RegenerateDocsModal = ({
  isOpen,
  loading,
  preview,
  selection,
  onToggle,
  submitting,
  result,
  onConfirm,
  onClose,
  changeSummary,
}) => {
  if (!isOpen) return null;

  const invoice = preview?.invoice;
  const rcti = preview?.rcti;
  const rows = [
    { key: 'invoice', doc: invoice },
    { key: 'rcti', doc: rcti },
  ].filter((r) => isRealDoc(r.doc));

  const anyRegenerable = rows.some((r) => r.doc.regenerable);
  const anySelected = !!(selection?.invoice || selection?.rcti);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
      <div className="relative mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
        <h3 className="text-lg leading-6 font-medium text-gray-900 text-center">Regenerate invoice / RCTI</h3>

        <div className="mt-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-6 text-gray-600">
              <Spinner className="h-5 w-5 text-jims-cyan" /> Checking affected documents…
            </div>
          )}

          {!loading && preview?.error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">{preview.error}</p>
          )}

          {/* Execution results */}
          {!loading && result && (
            <div className="space-y-2">
              <ResultRow docKey="invoice" res={result.invoice} />
              <ResultRow docKey="rcti" res={result.rcti} />
              {!result.invoice && !result.rcti && result.message && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3 text-left">{result.message}</p>
              )}
            </div>
          )}

          {/* Preview / selection */}
          {!loading && !result && !preview?.error && (
            <>
              <p className="text-sm text-gray-600 text-left">
                The {changeSummary || 'change'} affects the following draft {rows.length > 1 ? 'documents' : 'document'}. Choose which
                to regenerate — a new number is issued each time (e.g. <span className="font-mono">LP-RCTI-123(1)</span>).
              </p>
              {rows.length === 0 && (
                <p className="text-sm text-gray-500">No regenerable invoice or RCTI was found for this job.</p>
              )}
              {rows.map((r) => (
                <PreviewRow
                  key={r.key}
                  docKey={r.key}
                  doc={r.doc}
                  checked={!!selection?.[r.key]}
                  onToggle={onToggle}
                />
              ))}
              {rows.length > 0 && !anyRegenerable && (
                <p className="text-sm text-amber-700">
                  None of these can be regenerated (only Xero drafts are eligible). You can skip and adjust them in Xero.
                </p>
              )}
            </>
          )}
        </div>

        <div className="items-center px-4 py-3 mt-2 space-x-4 flex justify-center">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {result ? 'Close' : 'Skip'}
          </button>
          {!result && (
            <button
              onClick={onConfirm}
              disabled={submitting || loading || !anySelected}
              className="px-4 py-2 text-white rounded transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center bg-jims-cyan hover:bg-jims-cyan-dark focus:ring-jims-cyan"
            >
              {submitting ? (<><Spinner className="-ml-1 mr-2 h-4 w-4" />Regenerating…</>) : 'Regenerate Selected'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegenerateDocsModal;
